/**
 * SwingThumbnail Component
 * Video thumbnail with overlay checkbox and analysis status badge
 */

import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useEffect, useRef } from 'react';

interface SwingThumbnailProps {
  id: string;
  thumbnailUrl?: string | null;
  filename?: string | null;
  selected?: boolean;
  hasAnalysis: boolean;
  onClick?: () => void;
  className?: string;
}

export function SwingThumbnail({
  id,
  thumbnailUrl,
  filename,
  selected = false,
  hasAnalysis,
  onClick,
  className
}: SwingThumbnailProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current && thumbnailUrl) {
      // Set video to show first frame as thumbnail
      videoRef.current.currentTime = 0.1;
    }
  }, [thumbnailUrl]);

  const isVideoUrl = (() => {
    if (!thumbnailUrl) return false;
    const lower = thumbnailUrl.toLowerCase();
    return (
      lower.includes('.mp4') ||
      lower.includes('.m3u8') ||
      lower.includes('/manifest/') ||
      lower.includes('stream.mux.com') ||
      lower.includes('watch.cloudflarestream.com') ||
      lower.includes('iframe.cloudflarestream.com')
    );
  })();

  return (
    <div
      className={cn(
        "relative aspect-video rounded-lg overflow-hidden",
        "border-2 transition-all cursor-pointer",
        selected
          ? "border-primary shadow-lg scale-[1.02]"
          : "border-border hover:border-primary/50",
        className
      )}
      onClick={onClick}
    >
      {/* Thumbnail Image/Video */}
      {thumbnailUrl ? (
        isVideoUrl ? (
          <video
            ref={videoRef}
            src={thumbnailUrl}
            className="w-full h-full object-cover"
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={(e) => {
              // Seek to first frame to show thumbnail
              const video = e.currentTarget;
              video.currentTime = 0.1;
            }}
          />
        ) : (
          <img
            src={thumbnailUrl}
            alt={filename || `Swing ${id.slice(0, 8)}`}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails, try as video
              const img = e.currentTarget;
              if (!img.parentElement) return;
              const video = document.createElement('video');
              video.src = thumbnailUrl;
              video.className = 'w-full h-full object-cover';
              video.muted = true;
              video.playsInline = true;
              video.preload = 'metadata';
              video.onloadedmetadata = () => {
                video.currentTime = 0.1;
              };
              img.parentElement.replaceChild(video, img);
            }}
          />
        )
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center">
          <span className="text-muted-foreground text-sm">No thumbnail</span>
        </div>
      )}

      {/* Selected Overlay */}
      {selected && (
        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
          <div className="bg-primary rounded-full p-2">
            <Check className="w-5 h-5 text-primary-foreground" />
          </div>
        </div>
      )}

      {/* Analysis Status Badge */}
      {hasAnalysis && (
        <div className="absolute top-2 right-2">
          <div className="bg-success/90 text-success-foreground text-xs px-2 py-1 rounded-full">
            Analyzed
          </div>
        </div>
      )}

      {/* Filename Overlay (bottom) */}
      {filename && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-2 py-1 truncate">
          {filename}
        </div>
      )}
    </div>
  );
}


